/**
 * Clarion Elite Psychometric Career Assessment
 * Instruments used:
 *  - Holland RIASEC (Self-Directed Search, Holland 1994)        — 18 Likert items
 *  - Big Five Personality (BFI-10, Rammstedt & John 2007)       — 10 Likert items
 *  - Multiple Intelligences (Gardner 1983, MIDAS adapted)       — 20 Likert items
 *  - Cognitive Aptitude Battery (Numerical / Verbal / Logical / Abstract) — 20 MCQ items
 *
 * Total: 68 questions
 */

// ─── UI Constants ──────────────────────────────────────────────────────────────
export const QUESTIONS_PER_PAGE = 6;

export const LIKERT_LABELS = [
  { value: 1, label: 'Strongly Disagree', color: '#ef4444' },
  { value: 2, label: 'Disagree',          color: '#f97316' },
  { value: 3, label: 'Neutral',           color: '#eab308' },
  { value: 4, label: 'Agree',             color: '#22c55e' },
  { value: 5, label: 'Strongly Agree',    color: '#10b981' },
];

// ─── Sections ─────────────────────────────────────────────────────────────────
export const SECTIONS = [
  {
    id: 'activities',
    title: 'Career Interests',
    subtitle: 'Rate how much you enjoy each activity or work environment',
    desc: 'Identifies your natural career inclinations using the globally validated Holland RIASEC model.',
    color: '#7c3aed', icon: '🎯', range: [0, 17], type: 'likert',
  },
  {
    id: 'personality',
    title: 'Personality Profile',
    subtitle: 'Rate how accurately each statement describes you',
    desc: 'Maps your personality on the Big Five — the gold standard in personality psychology.',
    color: '#3b82f6', icon: '🧬', range: [18, 27], type: 'likert',
  },
  {
    id: 'intelligences',
    title: 'Intelligence Strengths',
    subtitle: 'Rate how naturally you excel in each domain',
    desc: 'Profiles your strengths across Gardner\'s 8 intelligences plus modern additions.',
    color: '#10b981', icon: '⚡', range: [28, 47], type: 'likert',
  },
  {
    id: 'aptitude',
    title: 'Cognitive Aptitude',
    subtitle: 'Select the best answer for each question',
    desc: 'Measures your numerical, verbal, logical and abstract reasoning abilities.',
    color: '#f59e0b', icon: '🧮', range: [48, 67], type: 'mcq',
  },
];

// ─── Likert Questions (Q0–Q47) ────────────────────────────────────────────────
export const LIKERT_QUESTIONS = [
  // Holland RIASEC — 18 items (3 per type)
  { id: 0,  dim: 'R', text: 'Working with tools, machines, or physical equipment' },
  { id: 1,  dim: 'R', text: 'Building, repairing, or assembling things with your hands' },
  { id: 2,  dim: 'R', text: 'Outdoor or sport-related work; physical, hands-on tasks' },
  { id: 3,  dim: 'I', text: 'Analysing complex problems and finding logical solutions' },
  { id: 4,  dim: 'I', text: 'Researching, experimenting, or discovering how things work' },
  { id: 5,  dim: 'I', text: 'Solving scientific, mathematical, or technical challenges' },
  { id: 6,  dim: 'A', text: 'Expressing ideas through writing, art, music, or design' },
  { id: 7,  dim: 'A', text: 'Creative work where you can invent or innovate freely' },
  { id: 8,  dim: 'A', text: 'Performing, directing, or producing original creative work' },
  { id: 9,  dim: 'S', text: 'Helping, guiding, or counselling people through challenges' },
  { id: 10, dim: 'S', text: 'Teaching, training, or explaining concepts to others' },
  { id: 11, dim: 'S', text: 'Working closely with people to improve their wellbeing' },
  { id: 12, dim: 'E', text: 'Leading a team or managing others to achieve goals' },
  { id: 13, dim: 'E', text: 'Persuading, pitching, or influencing people\'s decisions' },
  { id: 14, dim: 'E', text: 'Starting ventures, taking risks, or pursuing ambitious goals' },
  { id: 15, dim: 'C', text: 'Organising, managing, and maintaining records or systems' },
  { id: 16, dim: 'C', text: 'Working in structured, predictable, rule-based environments' },
  { id: 17, dim: 'C', text: 'Ensuring accuracy, following procedures, maintaining quality' },

  // Big Five (BFI-10) — 10 items
  { id: 18, dim: 'O',  text: 'I have a vivid imagination and enjoy exploring new ideas' },
  { id: 19, dim: 'O',  text: 'I am curious and actively seek out fresh experiences and perspectives' },
  { id: 20, dim: 'C5', text: 'I do a thorough job and pay very close attention to detail' },
  { id: 21, dim: 'C5', text: 'I am organised, reliable, and tend to plan things ahead carefully' },
  { id: 22, dim: 'E5', text: 'I am outgoing, sociable, and enjoy meeting new people' },
  { id: 23, dim: 'E5', text: 'I feel energised in social situations and am easy to talk to' },
  { id: 24, dim: 'A5', text: 'I trust people readily and tend to see the best in others' },
  { id: 25, dim: 'A5', text: 'I am cooperative, empathetic, and often put others\' needs first' },
  { id: 26, dim: 'ES', text: 'I stay calm under pressure and recover quickly from setbacks' },
  { id: 27, dim: 'ES', text: 'I handle stress well and rarely feel overwhelmed by challenges' },

  // Multiple Intelligences — 20 items (2 per intelligence)
  { id: 28, dim: 'MI_ling',  text: 'I enjoy reading, writing, storytelling, or debating ideas' },
  { id: 29, dim: 'MI_ling',  text: 'I express myself clearly and persuasively in words' },
  { id: 30, dim: 'MI_logic', text: 'I enjoy working with numbers, patterns, and logical puzzles' },
  { id: 31, dim: 'MI_logic', text: 'I think sequentially and like to analyse data step by step' },
  { id: 32, dim: 'MI_spat',  text: 'I think in images and can visualise objects or spaces in 3D easily' },
  { id: 33, dim: 'MI_spat',  text: 'I enjoy drawing, design, photography, or visual art' },
  { id: 34, dim: 'MI_music', text: 'I am sensitive to rhythm, melody, and tone in music' },
  { id: 35, dim: 'MI_music', text: 'I learn and remember songs or musical patterns with ease' },
  { id: 36, dim: 'MI_body',  text: 'I learn best through physical practice — doing rather than reading' },
  { id: 37, dim: 'MI_body',  text: 'I excel at sports, dance, crafts, or hands-on technical work' },
  { id: 38, dim: 'MI_inter', text: 'I intuitively understand other people\'s feelings and motivations' },
  { id: 39, dim: 'MI_inter', text: 'I am skilled at collaborating, mediating, and building rapport' },
  { id: 40, dim: 'MI_intra', text: 'I have a strong, clear sense of my own values, strengths and goals' },
  { id: 41, dim: 'MI_intra', text: 'I prefer to reflect deeply before making important decisions' },
  { id: 42, dim: 'MI_nat',   text: 'I am fascinated by nature, biology, animals, or ecosystems' },
  { id: 43, dim: 'MI_nat',   text: 'I notice subtle patterns in the natural world around me' },
  { id: 44, dim: 'MI_tech',  text: 'I pick up new technology and digital tools quickly and intuitively' },
  { id: 45, dim: 'MI_tech',  text: 'I enjoy programming, data analysis, or building digital systems' },
  { id: 46, dim: 'MI_lead',  text: 'I naturally take charge and organise people to accomplish goals' },
  { id: 47, dim: 'MI_lead',  text: 'I am comfortable making key decisions under pressure and uncertainty' },
];

// ─── Aptitude MCQ Questions (Q48–Q67) ─────────────────────────────────────────
// correct: 0-indexed answer index
export const APTITUDE_QUESTIONS = [
  // — Numerical Reasoning (5 questions) ——————————————————————————————————————
  {
    id: 48, category: 'numerical',
    text: 'A laptop costs ₹60,000 and is discounted by 15%. What is the sale price?',
    options: ['₹48,000', '₹51,000', '₹52,500', '₹54,000'],
    correct: 1,
    explanation: '15% of ₹60,000 = ₹9,000. Sale price = ₹60,000 − ₹9,000 = ₹51,000.',
  },
  {
    id: 49, category: 'numerical',
    text: 'A company\'s revenue grew from ₹40 lakhs to ₹52 lakhs. What is the percentage increase?',
    options: ['20%', '25%', '30%', '35%'],
    correct: 2,
    explanation: 'Increase = ₹12 L. % increase = (12/40) × 100 = 30%.',
  },
  {
    id: 50, category: 'numerical',
    text: 'If 12% of a number equals 84, what is the number?',
    options: ['600', '650', '700', '750'],
    correct: 2,
    explanation: 'x = 84 / 0.12 = 700.',
  },
  {
    id: 51, category: 'numerical',
    text: 'A train covers 360 km in 4 hours. At the same speed, how far does it travel in 7 hours?',
    options: ['560 km', '600 km', '630 km', '660 km',],
    correct: 2,
    explanation: 'Speed = 360/4 = 90 km/h. Distance = 90 × 7 = 630 km.',
  },
  {
    id: 52, category: 'numerical',
    text: 'What is the next number in the series: 3, 8, 15, 24, 35, ___?',
    options: ['46', '48', '50', '54'],
    correct: 1,
    explanation: 'Differences: 5, 7, 9, 11, 13. Next = 35 + 13 = 48.',
  },

  // — Verbal Reasoning (5 questions) ————————————————————————————————————————
  {
    id: 53, category: 'verbal',
    text: 'Choose the word most OPPOSITE in meaning to BENEVOLENT.',
    options: ['Generous', 'Malevolent', 'Indifferent', 'Negligent'],
    correct: 1,
    explanation: 'Benevolent = kind and charitable. Malevolent = having or showing a wish to do evil.',
  },
  {
    id: 54, category: 'verbal',
    text: 'BOOK is to LIBRARY as PAINTING is to ___.',
    options: ['Studio', 'Museum', 'Gallery', 'Exhibition'],
    correct: 2,
    explanation: 'A book belongs in a library; a painting belongs in a gallery.',
  },
  {
    id: 55, category: 'verbal',
    text: 'EPHEMERAL most closely means:',
    options: ['Eternal', 'Short-lived', 'Mysterious', 'Powerful'],
    correct: 1,
    explanation: 'Ephemeral = lasting for a very short time.',
  },
  {
    id: 56, category: 'verbal',
    text: 'All roses are flowers. Some flowers fade quickly. Which conclusion is valid?',
    options: [
      'All roses fade quickly',
      'Some roses may fade quickly',
      'No roses fade quickly',
      'Flowers are not roses',
    ],
    correct: 1,
    explanation: 'Since only SOME flowers fade, we can only conclude that SOME roses MAY fade.',
  },
  {
    id: 57, category: 'verbal',
    text: 'In a coded language, HONEST is written as IPOFTV. How is BRAVE written?',
    options: ['CSBWF', 'CSAWF', 'DSBWF', 'CSBVF'],
    correct: 0,
    explanation: 'Each letter is shifted +1: B→C, R→S, A→B, V→W, E→F = CSBWF.',
  },

  // — Logical Reasoning (5 questions) ——————————————————————————————————————
  {
    id: 58, category: 'logical',
    text: 'If it takes 5 workers 5 days to build 5 walls, how many days does it take 10 workers to build 10 walls?',
    options: ['2.5 days', '5 days', '10 days', '20 days'],
    correct: 1,
    explanation: 'Rate = 1 wall/worker/5 days. 10 workers × 5 days = 50 worker-days = 10 walls. Still 5 days.',
  },
  {
    id: 59, category: 'logical',
    text: 'A is taller than B. C is shorter than A. B is taller than C. Who is the shortest?',
    options: ['A', 'B', 'C', 'Cannot be determined'],
    correct: 2,
    explanation: 'Order: A > B > C. C is shortest.',
  },
  {
    id: 60, category: 'logical',
    text: 'THERMOMETER is to TEMPERATURE as BAROMETER is to:',
    options: ['Altitude', 'Atmospheric Pressure', 'Wind Speed', 'Humidity'],
    correct: 1,
    explanation: 'A barometer measures atmospheric pressure, just as a thermometer measures temperature.',
  },
  {
    id: 61, category: 'logical',
    text: 'Which number should replace the "?" in the series: 4, 9, 25, 49, 121, ?',
    options: ['144', '169', '196', '225'],
    correct: 1,
    explanation: 'These are squares of prime numbers: 2²=4, 3²=9, 5²=25, 7²=49, 11²=121, 13²=169.',
  },
  {
    id: 62, category: 'logical',
    text: 'If P > Q, Q > R, and R > S, which of the following is definitely true?',
    options: ['S > P', 'Q > S', 'P = S', 'R > P'],
    correct: 1,
    explanation: 'From Q > R > S, it follows that Q > S is definitely true.',
  },

  // — Abstract / Pattern Reasoning (5 questions) ——————————————————————————
  {
    id: 63, category: 'abstract',
    text: 'Find the odd one out: 8, 27, 64, 100, 125',
    options: ['8', '27', '100', '125'],
    correct: 2,
    explanation: '8=2³, 27=3³, 64=4³, 125=5³ are perfect cubes. 100 is not a perfect cube.',
  },
  {
    id: 64, category: 'abstract',
    text: 'What letter comes next in the sequence? A, C, F, J, O, ___',
    options: ['T', 'U', 'V', 'W'],
    correct: 1,
    explanation: 'Gaps: +2, +3, +4, +5, +6. O + 6 = U.',
  },
  {
    id: 65, category: 'abstract',
    text: 'A clock shows 3:15. What is the angle between the minute and hour hands?',
    options: ['0°', '7.5°', '15°', '22.5°'],
    correct: 1,
    explanation: 'At 3:15, minute hand is at 90°. Hour hand is at 90° + 7.5° (15 min × 0.5°/min) = 97.5°. Angle = 7.5°.',
  },
  {
    id: 66, category: 'abstract',
    text: 'In a group, every person shakes hands once with every other person. If there are 6 people, how many handshakes occur?',
    options: ['12', '15', '18', '21'],
    correct: 1,
    explanation: 'Combinations: C(6,2) = 6×5/2 = 15 handshakes.',
  },
  {
    id: 67, category: 'abstract',
    text: 'Which number replaces "?" — Row 1: 2, 4, 8 | Row 2: 3, 9, 27 | Row 3: 4, 16, ?',
    options: ['32', '48', '64', '128'],
    correct: 2,
    explanation: 'Pattern: col3 = col1³. Row 3: 4³ = 64.',
  },
];

// Combined questions array (Likert + MCQ)
export const QUESTIONS = [...LIKERT_QUESTIONS, ...APTITUDE_QUESTIONS];

// ─── Score Calculation ────────────────────────────────────────────────────────
export const calculateScores = (responses) => {
  const sum = (ids) => ids.reduce((acc, id) => acc + (parseInt(responses[id]) || 3), 0);
  const norm3 = (s, n) => Math.max(0, Math.min(100, Math.round(((s - n) / (n * 4)) * 100)));

  const holland = {
    R: norm3(sum([0,1,2]),  3),
    I: norm3(sum([3,4,5]),  3),
    A: norm3(sum([6,7,8]),  3),
    S: norm3(sum([9,10,11]),3),
    E: norm3(sum([12,13,14]),3),
    C: norm3(sum([15,16,17]),3),
  };

  const bigFive = {
    Openness:           norm3(sum([18,19]), 2),
    Conscientiousness:  norm3(sum([20,21]), 2),
    Extraversion:       norm3(sum([22,23]), 2),
    Agreeableness:      norm3(sum([24,25]), 2),
    EmotionalStability: norm3(sum([26,27]), 2),
  };

  const intelligences = {
    Linguistic:    norm3(sum([28,29]), 2),
    Logical:       norm3(sum([30,31]), 2),
    Spatial:       norm3(sum([32,33]), 2),
    Musical:       norm3(sum([34,35]), 2),
    Bodily:        norm3(sum([36,37]), 2),
    Interpersonal: norm3(sum([38,39]), 2),
    Intrapersonal: norm3(sum([40,41]), 2),
    Naturalistic:  norm3(sum([42,43]), 2),
    Technological: norm3(sum([44,45]), 2),
    Leadership:    norm3(sum([46,47]), 2),
  };

  // Aptitude: MCQ correct/wrong
  const aptCats = { numerical: [], verbal: [], logical: [], abstract: [] };
  APTITUDE_QUESTIONS.forEach(q => {
    const ans = responses[q.id];
    aptCats[q.category].push(ans === q.correct ? 1 : 0);
  });
  const aptPct = (arr) => arr.length ? Math.round((arr.reduce((a,b) => a+b, 0) / arr.length) * 100) : 0;
  const aptitude = {
    numerical: aptPct(aptCats.numerical),
    verbal:    aptPct(aptCats.verbal),
    logical:   aptPct(aptCats.logical),
    abstract:  aptPct(aptCats.abstract),
  };
  const aptitudeTotal = Math.round(Object.values(aptitude).reduce((a,b) => a+b, 0) / 4);

  return { holland, bigFive, intelligences, aptitude, aptitudeTotal };
};

// ─── Career Database ──────────────────────────────────────────────────────────
const CAREER_DB = [
  {
    id: 'data_science',       title: 'Data Science & Artificial Intelligence', icon: '🤖',
    hollandMatch: ['I','C','R'], miMatch: ['Logical','Technological'],
    aptBoost: ['numerical','logical'],
    description: 'Build AI systems, uncover patterns in large datasets, and engineer the intelligence layer of tomorrow\'s products.',
    degrees: ['BSc / MSc Data Science', 'MSc Artificial Intelligence', 'B.Tech Computer Science', 'MSc Machine Learning'],
    exams: ['GRE', 'TOEFL / IELTS', 'GMAT (MBA track)'],
    salaryRange: '₹12L – ₹60L+ (India) | $80K – $200K (global)',
    growth: 'Exceptional — 35% growth projected by 2032 (US BLS)',
    workStyle: 'Hybrid analytical + creative',
  },
  {
    id: 'engineering',        title: 'Engineering & Technology', icon: '⚙️',
    hollandMatch: ['R','I','C'], miMatch: ['Logical','Technological','Spatial'],
    aptBoost: ['numerical','abstract'],
    description: 'Design, build and optimise the physical and digital infrastructure that underpins modern civilisation.',
    degrees: ['B.Tech / BE (any branch)', 'MSc / MEng (specialised)', 'MBA Engineering Management'],
    exams: ['GRE', 'IELTS / TOEFL', 'SAT (undergraduate)'],
    salaryRange: '₹8L – ₹40L (India) | $70K – $150K (global)',
    growth: 'High — especially software, renewable energy, aerospace',
    workStyle: 'Technical + problem-solving',
  },
  {
    id: 'medicine',           title: 'Medicine & Health Sciences', icon: '🏥',
    hollandMatch: ['I','S','R'], miMatch: ['Logical','Interpersonal','Bodily'],
    aptBoost: ['verbal','logical'],
    description: 'Diagnose, treat, and prevent disease — one of the most respected and fulfilling professions worldwide.',
    degrees: ['MBBS / MD', 'BPharm / MPharm', 'BSc Nursing', 'MPH (Public Health)'],
    exams: ['USMLE (USA)', 'PLAB (UK)', 'AMC (Australia)', 'IELTS / OET'],
    salaryRange: '₹15L – ₹80L+ (India) | $120K – $300K (global)',
    growth: 'Very High — ageing global population drives sustained demand',
    workStyle: 'People-focused + scientific rigour',
  },
  {
    id: 'psychology',         title: 'Psychology & Counselling', icon: '🧠',
    hollandMatch: ['I','S','A'], miMatch: ['Interpersonal','Intrapersonal','Linguistic'],
    aptBoost: ['verbal','logical'],
    description: 'Understand the human mind and help individuals, organisations, and communities thrive psychologically.',
    degrees: ['BSc / BA Psychology', 'MSc Clinical Psychology', 'MSc Counselling', 'MBA Organisational Behaviour'],
    exams: ['GRE Psychology', 'IELTS / TOEFL'],
    salaryRange: '₹5L – ₹25L (India) | $55K – $120K (global)',
    growth: 'High — mental health awareness and demand surging globally',
    workStyle: 'Empathetic + analytical',
  },
  {
    id: 'business_management', title: 'Business, Strategy & Management', icon: '💼',
    hollandMatch: ['E','S','C'], miMatch: ['Leadership','Interpersonal','Logical'],
    aptBoost: ['numerical','verbal'],
    description: 'Drive strategy, lead organisations, and create measurable value across every industry and sector.',
    degrees: ['BBA / BCom', 'MBA (General or Specialised)', 'MSc Management', 'MSc Finance'],
    exams: ['GMAT', 'GRE', 'IELTS / TOEFL'],
    salaryRange: '₹10L – ₹50L+ (India) | $80K – $200K (global)',
    growth: 'Steady — management always in demand across sectors',
    workStyle: 'Strategic + people leadership',
  },
  {
    id: 'finance',            title: 'Finance, Banking & Investments', icon: '📊',
    hollandMatch: ['C','E','I'], miMatch: ['Logical','Leadership','Technological'],
    aptBoost: ['numerical','abstract'],
    description: 'Manage capital, model financial risk, and drive decisions for corporations, institutions, and governments.',
    degrees: ['BCom / BBA Finance', 'MSc Finance', 'CFA / ACCA / CA', 'MSc Accounting'],
    exams: ['GMAT', 'GRE', 'IELTS / TOEFL', 'CFA Level 1'],
    salaryRange: '₹10L – ₹60L+ (India) | $80K – $250K (global)',
    growth: 'High — fintech, ESG investing and sustainable finance expanding fast',
    workStyle: 'Quantitative + structured',
  },
  {
    id: 'law',                title: 'Law & Legal Practice', icon: '⚖️',
    hollandMatch: ['E','I','C'], miMatch: ['Linguistic','Logical','Interpersonal'],
    aptBoost: ['verbal','logical'],
    description: 'Advocate for justice, shape public policy, and navigate complex legal frameworks across industries.',
    degrees: ['LLB / LLM', 'BA LLB (Integrated)', 'MBA Corporate Law', 'MSc International Law'],
    exams: ['LSAT (USA)', 'IELTS / TOEFL', 'SQE (UK)'],
    salaryRange: '₹8L – ₹60L+ (India) | $80K – $250K (global)',
    growth: 'Steady — IP, international trade and tech law growing strongly',
    workStyle: 'Analytical + persuasive',
  },
  {
    id: 'design_ux',          title: 'Design, Architecture & UX', icon: '🎨',
    hollandMatch: ['A','R','I'], miMatch: ['Spatial','Technological','Logical'],
    aptBoost: ['abstract','verbal'],
    description: 'Shape the visual and spatial world — from city skylines to intuitive digital experiences.',
    degrees: ['B.Arch / M.Arch', 'BDes / MDes', 'MSc UX / HCI', 'BFA Product Design'],
    exams: ['Portfolio Review', 'IELTS / TOEFL', 'GRE (grad school)'],
    salaryRange: '₹7L – ₹35L (India) | $60K – $150K (global)',
    growth: 'High — UX and sustainable architecture booming globally',
    workStyle: 'Creative + technical',
  },
  {
    id: 'media_comms',        title: 'Media, Journalism & Communications', icon: '📢',
    hollandMatch: ['A','E','S'], miMatch: ['Linguistic','Interpersonal','Technological'],
    aptBoost: ['verbal','abstract'],
    description: 'Tell compelling stories, shape public discourse, and connect brands with audiences across platforms.',
    degrees: ['BA Journalism / Mass Comm', 'MA Media Studies', 'MSc Digital Marketing', 'BA Film & TV'],
    exams: ['IELTS / TOEFL', 'Portfolio (Film / Journalism)'],
    salaryRange: '₹5L – ₹25L (India) | $50K – $120K (global)',
    growth: 'Medium-High — digital content and creator economy expanding',
    workStyle: 'Expressive + collaborative',
  },
  {
    id: 'environment',        title: 'Environmental Science & Sustainability', icon: '🌿',
    hollandMatch: ['I','R','S'], miMatch: ['Naturalistic','Logical','Spatial'],
    aptBoost: ['numerical','logical'],
    description: 'Address the defining challenges of our era — climate change, biodiversity, and green energy transition.',
    degrees: ['BSc / MSc Environmental Science', 'MSc Sustainability', 'BSc Marine Biology', 'MSc Climate Policy'],
    exams: ['IELTS / TOEFL', 'GRE (Masters / PhD)'],
    salaryRange: '₹6L – ₹25L (India) | $55K – $110K (global)',
    growth: 'Very High — green economy investment surging globally',
    workStyle: 'Field + research-based',
  },
  {
    id: 'education',          title: 'Education, Research & Academia', icon: '📚',
    hollandMatch: ['S','I','A'], miMatch: ['Linguistic','Interpersonal','Intrapersonal'],
    aptBoost: ['verbal','logical'],
    description: 'Shape future generations through teaching, curriculum design, educational research, and policy.',
    degrees: ['B.Ed / M.Ed', 'MA Education', 'PhD (Academic track)', 'MEd Educational Leadership'],
    exams: ['IELTS / TOEFL', 'GRE (PhD programmes)'],
    salaryRange: '₹5L – ₹20L (India) | $55K – $100K (global)',
    growth: 'Steady — EdTech creating new opportunities globally',
    workStyle: 'Mentoring + intellectual',
  },
];

// ─── Report Generator ─────────────────────────────────────────────────────────
const topN = (obj, n = 2) =>
  Object.entries(obj).sort((a, b) => b[1] - a[1]).slice(0, n).map(([k]) => k);

// Percentile band labels
const pctBand = (score) => {
  if (score >= 85) return { label: 'Exceptional', color: '#7c3aed' };
  if (score >= 70) return { label: 'Strong',       color: '#10b981' };
  if (score >= 50) return { label: 'Moderate',     color: '#3b82f6' };
  if (score >= 30) return { label: 'Developing',   color: '#f59e0b' };
  return               { label: 'Emerging',        color: '#94a3b8' };
};

const hollandNames = { R:'Realistic', I:'Investigative', A:'Artistic', S:'Social', E:'Enterprising', C:'Conventional' };

export const generateReport = (scores) => {
  const { holland, bigFive, intelligences, aptitude, aptitudeTotal } = scores;

  // ── Holland type ──────────────────────────────────────────────────────────
  const hollandRanked = Object.entries(holland).sort((a, b) => b[1] - a[1]);
  const [primaryCode, secondaryCode] = hollandRanked.map(([k]) => k);
  const hollandType = primaryCode + secondaryCode;

  const hollandArchetypes = {
    R: 'The Builder',      I: 'The Thinker',     A: 'The Creator',
    S: 'The Helper',       E: 'The Leader',       C: 'The Organiser',
  };
  const hollandTypeLabel = hollandArchetypes[primaryCode] + ' (' + primaryCode + secondaryCode + ')';

  // ── Career matching ───────────────────────────────────────────────────────
  const careerScores = CAREER_DB.map(career => {
    const hollandScore = career.hollandMatch.reduce((acc, code, i) => {
      const weight = [3, 2, 1][i] || 1;
      return acc + (holland[code] || 0) * weight;
    }, 0) / 6;

    const miScore = career.miMatch.reduce((acc, mi) => {
      return acc + (intelligences[mi] || 0);
    }, 0) / (career.miMatch.length * 100);

    const aptScore = career.aptBoost.reduce((acc, cat) => {
      return acc + (aptitude[cat] || 0);
    }, 0) / (career.aptBoost.length * 100);

    const total = hollandScore * 0.55 + miScore * 30 + aptScore * 15;
    return { ...career, matchScore: Math.min(98, Math.max(20, Math.round(total))) };
  }).sort((a, b) => b.matchScore - a.matchScore);

  const topCareers = careerScores.slice(0, 5);

  // ── Personality insights ──────────────────────────────────────────────────
  const personalityInsights = [];
  if (bigFive.Openness >= 65)              personalityInsights.push('Highly creative and intellectually curious — you thrive when exploring novel ideas and uncharted territory.');
  else if (bigFive.Openness < 40)          personalityInsights.push('Grounded and pragmatic — you prefer proven approaches and deliver reliable, tangible outcomes.');
  if (bigFive.Conscientiousness >= 65)     personalityInsights.push('Organised and dependable — your ability to execute tasks with precision is a powerful professional asset.');
  else if (bigFive.Conscientiousness < 40) personalityInsights.push('Flexible and adaptable — you pivot quickly and thrive in dynamic, fast-changing environments.');
  if (bigFive.Extraversion >= 65)          personalityInsights.push('Energised by people and collaboration — a natural communicator who builds influence through connection.');
  else if (bigFive.Extraversion < 40)      personalityInsights.push('Reflective and focused — you produce exceptional deep work and are most effective in autonomous roles.');
  if (bigFive.Agreeableness >= 65)         personalityInsights.push('Highly empathetic and cooperative — you build strong long-term relationships and excel in team environments.');
  if (bigFive.EmotionalStability >= 65)    personalityInsights.push('Resilient under pressure — you maintain composure in high-stakes situations where others may falter.');
  else if (bigFive.EmotionalStability < 40) personalityInsights.push('Emotionally perceptive — your sensitivity to nuance makes you attuned to people\'s unspoken needs.');

  // ── Personality archetype ─────────────────────────────────────────────────
  const isOpen  = bigFive.Openness         >= 55;
  const isCons  = bigFive.Conscientiousness >= 55;
  const isExtra = bigFive.Extraversion      >= 55;
  const isAgree = bigFive.Agreeableness     >= 55;
  const isStable = bigFive.EmotionalStability >= 55;

  let personalityArchetype = '';
  let archetypeDesc = '';

  if (isExtra && isOpen && isCons) {
    personalityArchetype = 'The Visionary Executive';
    archetypeDesc = 'You combine creative ambition with the discipline to execute and the charisma to lead. Rare and powerful combination for entrepreneurship and senior leadership.';
  } else if (isOpen && !isExtra && isCons) {
    personalityArchetype = 'The Deep Thinker';
    archetypeDesc = 'You bring deep intellectual rigour and creative insight to complex problems. You work best with autonomy, producing breakthrough ideas through sustained focused effort.';
  } else if (isExtra && isAgree && !isOpen) {
    personalityArchetype = 'The People Champion';
    archetypeDesc = 'Your warmth, communication skills and empathy make you exceptional at building teams, resolving conflict, and creating environments where people thrive.';
  } else if (isCons && !isExtra && isStable) {
    personalityArchetype = 'The Reliable Architect';
    archetypeDesc = 'You build dependable systems, deliver with consistency, and maintain quality under pressure. Organisations rely on people like you to make strategy a reality.';
  } else if (isOpen && isExtra) {
    personalityArchetype = 'The Creative Catalyst';
    archetypeDesc = 'You generate energy, ideas and enthusiasm in any room. You inspire others to think bigger and are most effective when given space to innovate and persuade.';
  } else {
    personalityArchetype = 'The Balanced Professional';
    archetypeDesc = 'You bring a well-rounded set of traits that allow you to adapt to different contexts and roles. Your versatility is a strategic advantage in complex organisations.';
  }

  // ── Intelligence strengths ────────────────────────────────────────────────
  const topIntelligences = topN(intelligences, 3);

  const strengthMap = {
    Linguistic:    'Exceptional communication — persuasive writer, clear speaker, and compelling storyteller',
    Logical:       'Powerful analytical reasoning — you deconstruct complexity and build watertight arguments',
    Spatial:       'Strong visual-spatial intelligence — you think in 3D and excel at design and systems thinking',
    Musical:       'Deep pattern sensitivity — you detect subtle rhythms and structures others miss',
    Bodily:        'Superior kinaesthetic intelligence — you master physical skills rapidly and intuitively',
    Interpersonal: 'Natural social intelligence — you read people accurately and build authentic relationships',
    Intrapersonal: 'Exceptional self-awareness — you understand your own drivers with unusual clarity',
    Naturalistic:  'Systems-level environmental awareness — you notice intricate patterns in complex systems',
    Technological: 'Rapid digital fluency — you master new tools and platforms faster than most',
    Leadership:    'Innate decisiveness and mobilisation ability — you move people and resources to action',
  };
  const strengths = topIntelligences.map(mi => strengthMap[mi]).filter(Boolean);

  // ── Learning style ────────────────────────────────────────────────────────
  const [topMI] = topIntelligences;
  const learningStyleMap = {
    Linguistic:    'You learn best through reading, discussion, and writing. Use structured note-taking, debate, and text-rich resources.',
    Logical:       'Structured frameworks and case studies work best. Seek problems to solve — worked examples, coding challenges, and logical models.',
    Spatial:       'Visual diagrams, concept maps, and video content maximise your retention. Sketch summaries after every study session.',
    Musical:       'Rhythm-based memory aids and musical mnemonics support recall. Background music at a consistent tempo may improve focus.',
    Bodily:        'Hands-on labs, simulations, and real-world practice accelerate learning far beyond passive reading.',
    Interpersonal: 'Group study, peer teaching, and collaborative projects energise your learning and deepen understanding.',
    Intrapersonal: 'Self-paced, reflective study with journaling and independent projects suits you best. Protect deep-work blocks.',
    Naturalistic:  'Real-world examples, field-based learning, and systems-thinking frameworks deepen your understanding.',
    Technological: 'Interactive platforms, coding environments, and data tools are your natural habitat. Build as you learn.',
    Leadership:    'Project-based learning, leading study groups, and taking ownership of outcomes drives your best performance.',
  };
  const learningStyle = learningStyleMap[topMI] || 'A blend of visual, analytical, and hands-on approaches will yield your best results.';

  // ── Aptitude analysis ─────────────────────────────────────────────────────
  const aptBand = pctBand(aptitudeTotal);
  const aptSubBands = {
    numerical: pctBand(aptitude.numerical),
    verbal:    pctBand(aptitude.verbal),
    logical:   pctBand(aptitude.logical),
    abstract:  pctBand(aptitude.abstract),
  };

  const bestAptCat = Object.entries(aptitude).sort((a,b) => b[1]-a[1])[0][0];
  const aptCatLabels = { numerical:'Numerical Reasoning', verbal:'Verbal Reasoning', logical:'Logical Reasoning', abstract:'Abstract Reasoning' };

  const aptitudeAnalysis = 'Your overall cognitive aptitude score places you in the '
    + aptBand.label + ' band. Your strongest aptitude area is '
    + aptCatLabels[bestAptCat]
    + ' (' + aptitude[bestAptCat] + '%), which aligns closely with your career interest profile. '
    + (aptitudeTotal >= 70
      ? 'This cognitive profile makes you a strong candidate for rigorous academic programmes and competitive entrance exams.'
      : aptitudeTotal >= 50
      ? 'Focused practice on your lower-scoring aptitude categories will significantly strengthen your competitive exam performance.'
      : 'Dedicated preparation using targeted aptitude resources will build the cognitive tools needed for your chosen career path.');

  // ── Study abroad readiness ────────────────────────────────────────────────
  const abroadScore = Math.round(
    (bigFive.Openness + bigFive.EmotionalStability + aptitudeTotal * 0.6) / 2.6
  );
  const studyAbroadReadiness = {
    score: Math.min(99, abroadScore),
    band: pctBand(Math.min(99, abroadScore)),
    note: abroadScore >= 70
      ? 'Your profile shows strong adaptability and cognitive readiness for international study. You are well-suited to competitive programmes at top global universities.'
      : 'With targeted English proficiency preparation and aptitude practice, you can build a competitive international application.',
  };

  // ── Overall readiness score ───────────────────────────────────────────────
  const overallScore = Math.round(
    (aptitudeTotal * 0.35) +
    (Object.values(bigFive).reduce((a,b) => a+b, 0) / Object.keys(bigFive).length) * 0.25 +
    (Object.values(intelligences).sort((a,b) => b-a).slice(0,3).reduce((a,b)=>a+b,0) / 3) * 0.25 +
    (holland[primaryCode] * 0.15)
  );

  // ── Action plan ───────────────────────────────────────────────────────────
  const actionPlan = [
    {
      phase: 'Month 1–2 — Explore & Confirm',
      color: '#7c3aed',
      steps: [
        'Deep-dive into your top 2 career matches: read job descriptions, career forums, and alumni profiles',
        'Book an information interview with a professional in your #1 career match',
        'Research 3 specific university programmes for each shortlisted career',
        'Identify and register for aptitude preparation resources to improve your weaker categories',
      ],
    },
    {
      phase: 'Month 3–4 — Build & Apply',
      color: '#3b82f6',
      steps: [
        'Begin relevant extracurricular or online certifications aligned to your chosen field',
        'Prepare for required entrance exams (list shown in your career match cards above)',
        'Draft your personal statement around your Holland type and top intelligences',
        'Shortlist 5–8 specific universities and note their application deadlines',
      ],
    },
    {
      phase: 'Month 5–6 — Submit & Excel',
      color: '#10b981',
      steps: [
        'Finalise and submit applications before all deadlines',
        'Prepare for interviews using your personality archetype as a framework for self-presentation',
        'Apply for relevant scholarships — your counsellor will identify the best matches for your profile',
        'Review and align your social media profiles (LinkedIn) with your target career narrative',
      ],
    },
  ];

  // ── Cognitive profile text ─────────────────────────────────────────────────
  const cognitiveProfile = 'Your dominant intelligence areas — '
    + topIntelligences.slice(0, 2).join(' and ')
    + ' — combined with your '
    + (aptitudeTotal >= 70 ? 'strong' : 'developing')
    + ' aptitude scores suggest a '
    + (isOpen && aptitudeTotal >= 60 ? 'convergent-divergent' : aptitudeTotal >= 65 ? 'analytically-dominant' : 'practically-oriented')
    + ' cognitive style. '
    + 'You process information most effectively through '
    + (topMI === 'Logical' ? 'structured frameworks and sequential analysis'
      : topMI === 'Linguistic' ? 'language, narrative and discussion'
      : topMI === 'Spatial' ? 'visual models and spatial reasoning'
      : topMI === 'Interpersonal' ? 'social dialogue and collaborative sensemaking'
      : 'hands-on exploration and pattern recognition') + '.';

  // ── Serialise ranked arrays (Firestore safe) ──────────────────────────────
  const hollandRankedObj      = hollandRanked.map(([code, score]) => ({ code, score }));
  const bigFiveRankedObj      = Object.entries(bigFive).sort((a, b) => b[1] - a[1]).map(([trait, score]) => ({ trait, score }));
  const intellRankedObj       = Object.entries(intelligences).sort((a, b) => b[1] - a[1]).map(([intelligence, score]) => ({ intelligence, score }));
  const aptitudeRankedObj     = Object.entries(aptitude).map(([category, score]) => ({ category, score, band: pctBand(score) }));

  return {
    // Identification
    hollandType,
    hollandTypeLabel,
    personalityArchetype,
    archetypeDesc,

    // Ranked arrays for charts
    hollandRanked:        hollandRankedObj,
    bigFiveRanked:        bigFiveRankedObj,
    intelligencesRanked:  intellRankedObj,
    aptitudeRanked:       aptitudeRankedObj,

    // Flat score maps
    hollandScores:        Object.fromEntries(hollandRanked),
    bigFiveScores:        Object.fromEntries(Object.entries(bigFive)),
    intelligenceScores:   Object.fromEntries(Object.entries(intelligences)),
    aptitudeScores:       aptitude,
    aptitudeTotal,

    // Career matches
    topCareers,

    // Insights
    personalityInsights,
    strengths,
    learningStyle,
    cognitiveProfile,
    aptitudeAnalysis,
    aptBand,
    aptSubBands,

    // Extended
    studyAbroadReadiness,
    actionPlan,
    overallScore: Math.min(99, overallScore),
  };
};
